import { FC, useContext, useEffect, useMemo, useState } from 'react';
import Moment from 'react-moment';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Checkbox,
  createStyles,
  Group,
  MantineColor,
  MantineTheme,
  Menu,
  Text as MantineText,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useModals } from '@mantine/modals';
import { Trans } from '@lingui/react';
import {
  IconCalendarDue,
  IconChevronRight,
  IconDots,
  IconPencil,
  IconRepeat,
  IconTag,
  IconTrash,
  IconUser,
} from '@tabler/icons';
import { CustomModalHeader } from '../../Molecules/Dialogs';
import { AgendaItem } from '../../Types/AgendaItem';
import BookingEvent from '../../Types/Event';
import Contact from '../../Types/Contact';
import { fetchEventById, getBookingStateColour } from '../../Service/restapi/eventService';
import { fetchContactById } from '../../Service/restapi/contactService';
import { deleteTask } from '../../Service/restapi/taskService';
import { TaskListContext } from '../../Service/Context/TaskListContext';
import { isBeforeToday, isNDaysOrLessAfterToday, isToday, parseDate } from '../../Utils/dateUtils';
import useTaskConfirmDeleteModal from '../../Molecules/Event/Tasks/TaskConfirmDelete';
import { useBookitupSubscription } from '../../Service/Context/BookitupSubscriptionContext';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import { ProfileShare } from '../../Service/ProfileS';
import { EventS } from '../../Service/EventS';
import { RoutingS } from '../../Service/RoutingS';
import AutomaticLinkText from '../AutomaticLinkText';
import { InputHelp } from './InputHelp';

type Props = {
  task: AgendaItem;
  finishCallback: (patch: Record<string, boolean>) => Promise<unknown>;
  value: boolean;
  disabled: boolean;
  patchField: string;
};

const getTaskTitleColor = (theme: MantineTheme, completed: boolean) => {
  if (completed) {
    if (theme.colorScheme === 'dark') return theme.colors.dark[2];
    return theme.colors.gray[6];
  }

  if (theme.colorScheme === 'dark') return theme.colors.gray[0];
  return theme.colors.dark[8];
};

const getEventStatusLabel = (bookingEvent: BookingEvent) => {
  if (bookingEvent.eventState?.label) return bookingEvent.eventState.label;

  const defaultLabel = EventS.getBookingStateTooltipLabel(bookingEvent.bookingState);
  if (defaultLabel) return defaultLabel;

  const additionalLabels: Record<string, string> = {
    EARMARKED: 'Vorgemerkt',
    RECOMMENDED: 'Weiterempfohlen',
    PAYMENT: 'Zahlung',
    COMPLETED: 'Abgeschlossen',
  };

  return additionalLabels[bookingEvent.bookingState] || 'Status unbekannt';
};

const useStyles = createStyles((theme, { completed, busy }: { completed: boolean; busy: boolean }) => ({
  task: {
    display: 'grid',
    gridTemplateColumns: '30px minmax(0, 1fr) auto',
    alignItems: 'flex-start',
    gap: theme.spacing.md,
    padding: `${theme.spacing.lg}px ${theme.spacing.lg}px`,
    backgroundColor: 'transparent',
    opacity: busy ? 0.66 : 1,
    transition: 'background-color 150ms ease, opacity 150ms ease',

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      gridTemplateColumns: '28px minmax(0, 1fr) auto',
      gap: theme.spacing.sm,
      padding: `${theme.spacing.md}px 0`,
    },
  },

  checkbox: {
    marginTop: 2,

    '& input': {
      cursor: 'pointer',
    },
  },

  content: {
    minWidth: 0,
  },

  titleRow: {
    minWidth: 0,
  },

  titleButton: {
    minWidth: 0,
    maxWidth: '100%',
    borderRadius: theme.radius.sm,
    textAlign: 'left',

    '&:focus-visible': {
      outline: `2px solid ${theme.colors.blue[5]}`,
      outlineOffset: 3,
    },
  },

  title: {
    color: getTaskTitleColor(theme, completed),
    textDecoration: completed ? 'line-through' : 'none',
    textDecorationThickness: '1.5px',
    overflowWrap: 'anywhere',
    lineHeight: 1.45,
  },

  stateIcon: {
    flex: '0 0 auto',
  },

  priorityBadge: {
    flex: '0 0 auto',
    height: 20,
    marginTop: 1,
    paddingLeft: 7,
    paddingRight: 7,
    textTransform: 'none',
    letterSpacing: 0,
    fontWeight: 600,
  },

  notes: {
    maxWidth: 720,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[6],
    textDecoration: completed ? 'line-through' : 'none',
    lineHeight: 1.55,
    overflowWrap: 'anywhere',
  },

  metadataItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[6],
  },

  dueDate: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontWeight: 500,
  },

  primaryMetadata: {
    marginTop: 5,
  },

  eventLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    width: 'fit-content',
    maxWidth: '100%',
    minWidth: 0,
    marginTop: 5,
    padding: '3px 7px',
    borderRadius: 999,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.dark[6],
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
    textAlign: 'left',
    transition: 'color 120ms ease, background-color 120ms ease',

    '&:hover': {
      color: theme.colorScheme === 'dark' ? theme.colors.blue[2] : theme.colors.blue[7],
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.blue[0],
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.colors.blue[5]}`,
      outlineOffset: 3,
    },
  },

  eventText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'inherit',
    lineHeight: 1.4,
  },

  statusDot: {
    width: 8,
    height: 8,
    flex: '0 0 auto',
    borderRadius: '50%',
  },

  eventChevron: {
    flex: '0 0 auto',
    color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[5],
  },

  eventLabel: {
    opacity: 0.68,
    fontWeight: 400,
  },

  eventValue: {
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },

  person: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    maxWidth: 230,
    minWidth: 0,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[6],
  },

  secondaryMetadata: {
    marginTop: theme.spacing.sm,
  },

  moreAction: {
    marginTop: -3,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[5],

    '&:hover': {
      color: theme.colorScheme === 'dark' ? theme.colors.gray[0] : theme.colors.dark[7],
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1],
    },
  },
}));

const getApplicationUserPreview = (profileShare?: ProfileShare) => {
  if (!profileShare) {
    return undefined;
  }

  const displayName = [profileShare.firstName, profileShare.lastName].filter(Boolean).join(' ').trim();
  const initials = `${profileShare.firstName?.charAt(0) || ''}${profileShare.lastName?.charAt(0) || ''}`;

  return {
    label: displayName || profileShare.email,
    initials: initials || profileShare.email.charAt(0).toUpperCase(),
  };
};

const EditableTaskListItem: FC<Props> = ({
  finishCallback,
  value: checked,
  disabled,
  patchField,
  task: initialTask,
}) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState<AgendaItem>(initialTask);
  const [event, setEvent] = useState<BookingEvent>();
  const [contact, setContact] = useState<Contact>();
  const { classes } = useStyles({ completed: checked, busy: loading });

  const { refresh, taskListContent } = useContext(TaskListContext);
  const getGroupById = (groupId: string) => taskListContent.taskGroups.find((group) => group.id === groupId);
  const { openContextModal, closeAll } = useModals();
  const { openConfirmDeleteModal } = useTaskConfirmDeleteModal();
  const { profile, bookitupTeam } = useContext(ProfileContext);
  const { teamAddonPresent } = useBookitupSubscription();
  const profileShares: ProfileShare[] = bookitupTeam.shares;

  const assignedApplicationUserPreview = useMemo(
    () =>
      getApplicationUserPreview(
        profileShares.find((profileShare) => profileShare.applicationUserId === task.applicationUserId),
      ),
    [profileShares, task.applicationUserId],
  );
  const primaryEventDate = useMemo(() => (event ? EventS.getPrimaryDate(event) : undefined), [event]);

  useEffect(() => {
    setTask(initialTask);
  }, [initialTask]);

  useEffect(() => {
    if (!task.applicationUserId) {
      return undefined;
    }

    bookitupTeam.ensureSharesLoaded().catch(() => undefined);
    return undefined;
  }, [bookitupTeam, task.applicationUserId]);

  useEffect(() => {
    setContact(undefined);

    if (task.entityType !== 'events') {
      setEvent(undefined);
      return;
    }

    fetchEventById(task.entityId)
      .then((response) => {
        if (response.ok) {
          response.json().then(setEvent);
        } else {
          console.info(`could not load event ${task.entityId}`);
        }
      })
      .catch(() => console.info(`could not load event ${task.entityId}`));
  }, [task.entityId, task.entityType]);

  useEffect(() => {
    if (event?.customerId) {
      fetchContactById(event.customerId.toString()).then((loadedContact) => setContact(loadedContact as Contact));
    }
  }, [event]);

  const dueDate = task.dueDate ? parseDate(task.dueDate) : null;
  const dueDateColor = (): MantineColor => {
    if (!dueDate) return 'gray';
    if (isBeforeToday(dueDate) || isToday(dueDate)) return 'red';
    if (isNDaysOrLessAfterToday(3, dueDate)) return 'orange';
    return 'blue';
  };

  const dueDateLabel = () => {
    if (!dueDate) return '';
    if (isBeforeToday(dueDate)) return 'Überfällig';
    if (isToday(dueDate)) return 'Heute fällig';
    return 'Fällig';
  };

  const openTaskEditModal = () =>
    openContextModal('taskEditModal', {
      title: (
        <CustomModalHeader>
          <Trans id="task.edit" />
        </CustomModalHeader>
      ),
      innerProps: {
        profile,
        bookitupTeam,
        teamAddonPresent,
        initialTask: task,
        entityType: 'events',
        group: task.groupId ? getGroupById(task.groupId) : undefined,
        finish: (updatedTask: AgendaItem) => {
          if (updatedTask) {
            setTask(updatedTask);
          } else {
            refresh();
          }
        },
        showEntityTypeSelection: true,
      },
      centered: true,
    });

  const toggleCompleted = () => {
    setError(undefined);
    setLoading(true);
    finishCallback({ [patchField]: !checked })
      .catch((caughtError: Error) => setError(caughtError.message))
      .finally(() => setLoading(false));
  };

  const deleteThisTask = () => {
    closeAll();
    setLoading(true);
    deleteTask(task.id)
      .then(refresh)
      .finally(() => setLoading(false));
  };

  const onDeleteClick = () => openConfirmDeleteModal(deleteThisTask, closeAll);
  const openRelatedEvent = () => {
    if (event) {
      RoutingS.openInNewTab(`${process.env.REACT_APP_FRONTEND_URL}/events/${event.id}/details`);
    }
  };

  return (
    <Box className={classes.task} component="article" aria-busy={loading}>
      <Checkbox
        className={classes.checkbox}
        aria-label={checked ? `Aufgabe „${task.name}“ als offen markieren` : `Aufgabe „${task.name}“ erledigen`}
        radius="xl"
        size="md"
        color="green"
        onChange={toggleCompleted}
        disabled={disabled || loading}
        checked={checked}
      />

      <Box className={classes.content}>
        <Group className={classes.titleRow} spacing={8} noWrap align="flex-start">
          {task.recurringTaskId && (
            <Tooltip label="Wiederkehrende Aufgabe" withArrow>
              <Box
                className={classes.stateIcon}
                mt={3}
                sx={(theme) => ({
                  color: theme.colorScheme === 'dark' ? theme.colors.violet[3] : theme.colors.violet[6],
                })}
              >
                <IconRepeat size={17} stroke={2.1} aria-label="Wiederkehrende Aufgabe" />
              </Box>
            </Tooltip>
          )}
          <UnstyledButton
            className={classes.titleButton}
            onClick={openTaskEditModal}
            aria-label={`${task.name} bearbeiten`}
          >
            <MantineText className={classes.title} size="md" weight={600}>
              {task.name}
            </MantineText>
          </UnstyledButton>
        </Group>

        {event && contact && (
          <UnstyledButton
            className={classes.eventLink}
            onClick={openRelatedEvent}
            title={`${event.name || 'Zugehöriges Event'} öffnen`}
            aria-label={`${event.name || 'Zugehöriges Event'} öffnen`}
          >
            <Box
              className={classes.statusDot}
              sx={{ backgroundColor: event.eventState?.color || getBookingStateColour(event.bookingState) }}
            />
            <MantineText className={classes.eventText} size="xs">
              {getEventStatusLabel(event)} · {contact.personDescription}
              {event.name ? ` · ${event.name}` : ''} ·{' '}
              {primaryEventDate ? <Moment format="DD.MM.YYYY" date={new Date(primaryEventDate.date)} /> : 'Ohne Datum'}
            </MantineText>
            <IconChevronRight className={classes.eventChevron} size={14} stroke={1.8} />
          </UnstyledButton>
        )}

        {(task.groupId || task.dueDate) && (
          <Group className={classes.primaryMetadata} spacing="md">
            {task.priority && (
              <Badge className={classes.priorityBadge} size="sm" radius="sm" color="red" variant="filled">
                Wichtig
              </Badge>
            )}
            {task.groupId && (
              <Box className={classes.metadataItem}>
                <IconTag size={14} stroke={1.8} />
                <MantineText className={classes.eventValue} size="xs" inherit>
                  {task.groupName}
                </MantineText>
              </Box>
            )}

            {task.dueDate && (
              <Box
                className={classes.dueDate}
                sx={(theme) => ({
                  color: theme.colors[dueDateColor()][theme.colorScheme === 'dark' ? 4 : 7],
                })}
              >
                <IconCalendarDue size={15} stroke={1.8} />
                <MantineText size="xs" inherit>
                  {dueDateLabel()} · <Moment format="DD.MM.YYYY">{task.dueDate}</Moment>
                </MantineText>
              </Box>
            )}
          </Group>
        )}

        {task.notes && (
          <MantineText className={classes.notes} size="sm" mt="sm" lineClamp={2}>
            <AutomaticLinkText text={task.notes} />
          </MantineText>
        )}

        {assignedApplicationUserPreview && (
          <Group className={classes.secondaryMetadata} spacing="md">
            <Box className={classes.person} title={assignedApplicationUserPreview.label}>
              <Avatar
                size={18}
                radius="xl"
                sx={(theme) => ({
                  backgroundColor: theme.colorScheme === 'light' ? '#DFEBFB' : theme.colors.blue[8],
                  color: theme.colorScheme === 'light' ? '#1D5191' : theme.colors.blue[1],
                })}
              >
                {assignedApplicationUserPreview.initials || <IconUser size={13} />}
              </Avatar>
              <MantineText className={classes.eventLabel} size="xs" inherit>
                Zuständig
              </MantineText>
              <MantineText className={classes.eventValue} size="xs" inherit>
                {assignedApplicationUserPreview.label}
              </MantineText>
            </Box>
          </Group>
        )}

        {error && <InputHelp errorDescription={error} />}
      </Box>

      <Menu position="bottom-end" withinPortal shadow="md">
        <Menu.Target>
          <ActionIcon
            className={classes.moreAction}
            size={34}
            radius="md"
            variant="subtle"
            disabled={loading}
            aria-label="Weitere Aktionen"
          >
            <IconDots size={19} stroke={2} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item icon={<IconPencil size={15} />} onClick={openTaskEditModal}>
            Bearbeiten
          </Menu.Item>
          <Menu.Item color="red" icon={<IconTrash size={15} />} onClick={onDeleteClick}>
            Löschen
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
};

export default EditableTaskListItem;
